(() => {
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const ease = (value) => value * value * (3 - 2 * value);
  const mix = (from, to, amount) => from + (to - from) * amount;

  class SpinnerJourney {
    constructor() {
      this.main = document.querySelector('#MainContent');
      this.source = this.main?.querySelector('anda-spinner');
      this.object = this.source?.querySelector('.anda-showcase__object');
      const image = this.source?.querySelector('.anda-spinner-image');
      if (!image || !this.object) return;

      this.abort = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.frame = 0;
      this.scroll = null;
      this.previousTime = 0;
      this.handoff = 0;
      this.tilt = this.source.querySelector('.anda-showcase__tilt');
      this.suspension = this.source.querySelector('.anda-showcase__suspension');
      this.layer = document.createElement('div');
      this.layer.className = 'anda-journey';
      this.layer.setAttribute('aria-hidden', 'true');
      this.actor = document.createElement('div');
      this.actor.className = 'anda-journey__actor';
      this.rotor = document.createElement('div');
      this.rotor.className = 'anda-journey__rotor';
      const copy = image.cloneNode();
      copy.className = 'anda-journey__image';
      copy.alt = '';
      copy.removeAttribute('id');
      copy.removeAttribute('fetchpriority');
      copy.loading = 'eager';
      this.rotor.append(copy);
      this.actor.append(this.rotor);
      this.layer.append(this.actor);
      this.main.prepend(this.layer);
      this.main.classList.add('anda-scroll-scene');

      this.tick = this.tick.bind(this);
      this.schedule = this.schedule.bind(this);
      this.measure = this.measure.bind(this);
      const options = { signal: this.abort.signal, passive: true };
      // Capture handles both the desktop .page-wrapper and mobile window scroll.
      document.addEventListener('scroll', this.schedule, { ...options, capture: true });
      window.addEventListener('resize', this.measure, options);
      window.addEventListener('pageshow', this.measure, options);
      document.addEventListener('visibilitychange', this.schedule, options);
      this.source.addEventListener('anda:motion-change', this.schedule, options);
      this.suspension?.addEventListener('animationend', this.schedule, options);
      this.motion.addEventListener('change', this.schedule, options);
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.measure);
        this.observer.observe(this.main);
      }
      this.measure();
    }

    measure() {
      const main = this.main.getBoundingClientRect();
      // Measure the resting hero position, not its current journey transform.
      const transform = this.object.style.transform;
      this.object.style.removeProperty('transform');
      const object = this.object.getBoundingClientRect();
      this.object.style.transform = transform;
      this.width = main.width;
      this.height = window.innerHeight;
      this.size = object.width;
      this.originY = object.top - main.top + object.height / 2;
      this.originX = object.left - main.left + object.width / 2;
      const originX = this.originX / this.width;
      const heroHeight = this.source.closest('.anda-showcase').getBoundingClientRect().height;
      // Position, size, rotation and opacity are interpolated between real sections.
      this.points = [{ at: 0, x: originX, y: .5, scale: 1, turn: 0, opacity: 1 }];
      const add = (at, x, y, scale, turn, opacity) => {
        const previous = this.points.at(-1);
        this.points.push({ at: Math.max(previous.at + 80, at), x, y, scale, turn, opacity });
      };
      const section = (id) => {
        const node = this.main.querySelector(id);
        if (!node) return null;
        const rect = node.getBoundingClientRect();
        return { top: rect.top - main.top, height: rect.height };
      };
      const bounds = (selector) => {
        const node = this.main.querySelector(selector);
        if (!node) return null;
        const rect = node.getBoundingClientRect();
        return { left: rect.left - main.left, right: rect.right - main.left,
          top: rect.top - main.top, bottom: rect.bottom - main.top, width: rect.width };
      };
      const grid = bounds('.anda-product-grid');
      const leftMargin = grid ? clamp(grid.left / 2 / this.width, .02, .12) : .06;
      const rightMargin = grid ? clamp((grid.right + this.width) / 2 / this.width, .88, .98) : .94;
      // Exactly one destination per section, plus the hero's starting position.
      const collection = section('#anda-collection');
      if (collection) add(collection.top + collection.height * .25 - this.height * .15, rightMargin, .58, 1.3, 200, .20);
      const philosophy = section('#anda-philosophy');
      if (philosophy) {
        const art = bounds('.anda-detail-art');
        const copy = bounds('.anda-philosophy__copy');
        const sideBySide = art && copy && copy.left >= art.right;
        const x = sideBySide ? (copy.left + copy.right) / 2 / this.width : leftMargin;
        const at = philosophy.top + philosophy.height * .35;
        const y = sideBySide ? clamp((copy.bottom + this.size * .3 - at) / this.height, .6, .83) : .65;
        add(at, x, y, .62, 460, .20);
      }
      const guide = section('#anda-guide');
      if (guide) {
        const heading = bounds('#anda-guide h2');
        const questions = bounds('.anda-guide__questions');
        const sideBySide = heading && questions && questions.left >= heading.right;
        const x = heading ? (heading.left + heading.right) / 2 / this.width : .25;
        const scale = heading ? clamp(heading.width * 1.15 / this.size, .65, 1.55) : 1.55;
        const headingNode = this.main.querySelector('#anda-guide h2');
        let headingBottom = guide.top;
        if (headingNode) {
          // Measure the text, since the grid cell can stretch to the FAQ height.
          const range = document.createRange();
          range.selectNodeContents(headingNode);
          headingBottom = range.getBoundingClientRect().bottom - main.top;
        }
        add(guide.top, x, .65, scale, 710, sideBySide ? .20 : .14);
        this.points.at(-1).anchorY = headingBottom + this.size * scale * .38;
      }
      // The final destination belongs to the section, even when scrolling onward.
      const last = this.points.at(-1);
      last.anchorY ??= last.at + last.y * this.height;
      // Finish fading while the collection enters, independently of its destination.
      this.fadeEnd = Math.max(80, (collection?.top ?? heroHeight) - this.height * .25);
      this.fadeStart = Math.max(0, this.fadeEnd - heroHeight * .25);
      this.actor.style.width = `${this.size}px`;
      this.schedule();
    }

    schedule() {
      if (this.motion.matches || this.source.paused || document.hidden) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.previousTime = 0;
        if (this.motion.matches || this.source.paused) {
          this.actor.style.opacity = '0';
          this.resetSource();
          this.handoff = 0;
          this.scroll = null;
        }
        return;
      }
      if (!this.frame) this.frame = requestAnimationFrame(this.tick);
    }

    tick(time) {
      this.frame = 0;
      const actual = Math.max(0, -this.main.getBoundingClientRect().top);
      const delta = this.previousTime ? Math.min((time - this.previousTime) / 1000, .05) : 1 / 60;
      this.previousTime = time;
      if (this.scroll === null) this.scroll = actual;
      this.scroll = mix(this.scroll, actual, 1 - Math.exp(-delta * 12));
      if (Math.abs(actual - this.scroll) < .15) this.scroll = actual;

      let start = this.points[0];
      let end = this.points.at(-1);
      for (let index = 1; index < this.points.length; index++) {
        end = this.points[index];
        if (this.scroll <= end.at) break;
        start = end;
      }
      const progress = ease(clamp((this.scroll - start.at) / Math.max(1, end.at - start.at), 0, 1));
      const x = mix(start.x, end.x, progress) * this.width;
      const startY = start.anchorY ?? (start.at === 0 ? this.originY + actual * .82 : actual + start.y * this.height);
      const endY = end.anchorY ?? (actual + end.y * this.height);
      const y = mix(startY, endY, progress);
      const scale = mix(start.scale, end.scale, progress);
      const scrollTurn = mix(start.turn, end.turn, progress);
      const turn = scrollTurn + (this.source.angle || 0);
      const introRunning = this.suspension?.getAnimations().some(animation => animation.playState === 'running');
      // Opacity follows real scroll immediately, even during the entrance animation.
      const fadeProgress = ease(clamp((actual - this.fadeStart) / (this.fadeEnd - this.fadeStart), 0, 1));
      const desiredHandoff = introRunning ? 0 : fadeProgress;
      this.handoff = mix(this.handoff, desiredHandoff, 1 - Math.exp(-delta * 7));
      if (Math.abs(this.handoff - desiredHandoff) < .002) this.handoff = desiredHandoff;
      const backgroundOpacity = Math.min(.20, mix(start.opacity, end.opacity, progress));
      const brightness = mix(1, backgroundOpacity, fadeProgress);

      // Both renderings share the exact path. The real, clickable hero moves first;
      // the decorative copy gradually takes over on the layer behind the products.
      this.object.style.transform = `translate(-50%, -50%) translate3d(${x - this.originX}px, ${y - this.originY}px, 0) scale(${scale})`;
      this.object.style.opacity = String((1 - this.handoff) * brightness);
      this.object.style.pointerEvents = this.handoff > .95 ? 'none' : '';
      this.object.inert = this.handoff > .95;
      this.source.classList.toggle('anda-journey-active', this.scroll > 0);
      this.tilt?.style.setProperty('--anda-scroll-turn', `${scrollTurn}deg`);
      this.actor.style.transform = `translate3d(${x - this.size / 2}px, ${y - this.size / 2}px, 0) scale(${scale})`;
      this.actor.style.opacity = String(this.handoff * brightness);
      const tiltX = parseFloat(this.tilt?.style.getPropertyValue('--anda-tilt-x')) || 0;
      const tiltY = parseFloat(this.tilt?.style.getPropertyValue('--anda-tilt-y')) || 0;
      this.rotor.style.transform = `perspective(1100px) rotateX(${16 + tiltY}deg) rotateY(${-12 + tiltX}deg) rotateZ(${turn}deg)`;
      this.rotor.style.setProperty('--anda-spin-blur', this.source.rotor?.style.getPropertyValue('--anda-spin-blur') || '0px');
      if (this.scroll !== actual || this.handoff !== desiredHandoff) this.schedule();
      else this.previousTime = 0;
    }

    resetSource() {
      this.object?.style.removeProperty('opacity');
      this.object?.style.removeProperty('transform');
      this.object?.style.removeProperty('pointer-events');
      this.tilt?.style.removeProperty('--anda-scroll-turn');
      if (this.object) this.object.inert = false;
      this.source?.classList.remove('anda-journey-active');
    }

    destroy() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.layer?.remove();
      this.main?.classList.remove('anda-scroll-scene');
      this.resetSource();
    }
  }

  let journey = new SpinnerJourney();
  document.addEventListener('shopify:section:unload', () => journey.destroy());
  document.addEventListener('shopify:section:load', () => {
    journey.destroy();
    journey = new SpinnerJourney();
  });
})();
